import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { authAPI } from '@/shared/services/api';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, Loader2, Mail, Package } from 'lucide-react';

const ForgotPasswordPage = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      await authAPI.forgotPassword(email);
      setSent(true);
      toast.success(t('auth:resetLinkSent'));
    } catch (error) {
      toast.error(error.message || t('auth:resetRequestFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md bg-card border-border backdrop-blur">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-accent/80 flex items-center justify-center shadow-lg shadow-accent/20">
            <Package className="h-8 w-8 text-accent-foreground" />
          </div>
          <CardTitle className="text-3xl font-bold text-foreground font-['Space_Grotesk'] italic leading-none">EzERP</CardTitle>
          <CardDescription className="text-muted-foreground text-[10px] font-bold tracking-widest uppercase mt-1">
            {t('auth:passwordRecovery')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-5 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h1 className="text-xl font-bold text-foreground">{t('auth:checkEmailTitle')}</h1>
                <p className="text-sm text-muted-foreground">{t('auth:checkEmailSubtitle')}</p>
              </div>
              <Button asChild variant="outline" className="w-full h-12 rounded-xl font-bold">
                <Link to="/login">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('auth:backToLogin')}
                </Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent">
                  <Mail className="h-6 w-6" />
                </div>
                <h1 className="text-xl font-bold text-foreground">{t('auth:forgotPasswordTitle')}</h1>
                <p className="text-sm text-muted-foreground">{t('auth:forgotPasswordSubtitle')}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reset-email" className="text-muted-foreground font-bold text-[10px] uppercase tracking-widest">
                  {t('auth:email')}
                </Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  required
                  className="bg-muted/50 border-border text-foreground placeholder:text-muted-foreground h-12 rounded-xl focus-visible:ring-accent/50"
                  data-testid="forgot-password-email"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-accent hover:bg-accent/90 text-accent-foreground h-12 rounded-xl font-bold shadow-lg shadow-accent/20"
                disabled={loading}
                data-testid="forgot-password-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth:sendingResetLink')}
                  </>
                ) : (
                  t('auth:sendResetLink')
                )}
              </Button>
              <Button asChild variant="ghost" className="w-full h-10 rounded-xl font-semibold">
                <Link to="/login">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('auth:backToLogin')}
                </Link>
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
